'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { Trans, useTranslation } from 'react-i18next'
import { Button, ButtonLink, Field, Input } from '@kiao-voice/ui-kit'
import { EMAIL_VERIFICATION_REQUIRED_ERROR } from '@kiao-voice/core'
import { useAuthModal } from '@/features/auth/auth-modal-provider'
import {
  EmailVerificationStep,
  type EmailVerificationStepProps,
} from '@/features/auth/email-verification'
import { ANALYTICS_EVENTS, trackEvent, trackEventWhenReady } from '@/lib/analytics'
import { APP_ROUTES } from '@/lib/app-routes'
import { claimPublicCampaign, CampaignApiError, type PublicCampaign } from './api-client'
import { useCampaignViewer, type CampaignViewerView } from './use-campaign-viewer'
import { claimedPercent, daysUntil, formatCampaignDate } from './format'
import { CampaignToast } from './campaign-toast'

/**
 * The campaign section: one component, several states, exactly as the design
 * board specifies (`data-state` on the section drives the CSS).
 *
 *   idle → (guest) OTP modal → claiming → claimed | failed
 *   idle → (signed in, no plan) claiming → claimed | failed
 *
 * A signed-in visitor who already subscribes sees the campaign copy without a
 * claim form — the offer cannot apply to them, and an entry that always fails
 * is worse than no entry.
 */

type Phase =
  | { kind: 'idle' }
  /** The OTP modal is open; the section sits behind it in its sending state. */
  | { kind: 'verifying' }
  | { kind: 'claiming' }
  | { kind: 'claimed'; until: string; email: string; accountWasCreated: boolean }
  | { kind: 'failed'; code: CampaignApiError['code'] }
  /**
   * The claim would take an invite emailed to this address, and the signed-in
   * account has not proven that inbox: verify in place, then claim again.
   */
  | { kind: 'verifyEmail'; claimantEmail: string }

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Where this panel is mounted. Every campaign event carries it because the same
 * campaign converts very differently depending on what surrounds it — a home
 * page where it IS the content, a pricing page where it sits under the plans,
 * and its own landing page that people arrive at from an ad.
 */
export type CampaignSurface = 'home' | 'pricing' | 'landing_page'

export interface CampaignBannerProps {
  campaign: PublicCampaign
  /** Which page this panel is on — see {@link CampaignSurface}. */
  surface: CampaignSurface
  /** Anchor id on the section — the pricing page links to `#trial`. */
  sectionId?: string
  /** Extra class on the section, for per-surface spacing. */
  className?: string
  /**
   * Render nothing for a visitor who is already PAYING. Used by the pricing
   * page: someone on a paid plan is here to look at plans, and a panel under
   * the price cards telling them they already have one is noise.
   *
   * Deliberately NOT applied to a campaign grant. Someone whose gift is
   * counting down is exactly who the pricing page is for — 「還剩 N 天」next to
   * the plans is the conversion moment, not clutter. The home page shows every
   * returning state, because there the campaign IS the content.
   */
  hideForPaidSubscribers?: boolean
  /** Called after a successful claim so the host can refresh the quota. */
  onClaimed?: () => void
  /** Test seams. */
  claimFn?: typeof claimPublicCampaign
  viewerOverride?: CampaignViewerView
  requestVerificationFn?: EmailVerificationStepProps['requestFn']
  verifyFn?: EmailVerificationStepProps['verifyFn']
}

export function CampaignBanner({
  campaign,
  surface,
  sectionId,
  className,
  hideForPaidSubscribers = false,
  onClaimed,
  claimFn = claimPublicCampaign,
  viewerOverride,
  requestVerificationFn,
  verifyFn,
}: CampaignBannerProps) {
  const { t } = useTranslation('web')
  const authModal = useAuthModal()
  const resolvedViewer = useCampaignViewer(campaign.slug, { enabled: !viewerOverride })
  const viewer = viewerOverride ?? resolvedViewer

  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [email, setEmail] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [toastOpen, setToastOpen] = useState(false)
  /**
   * Every address already reported as a lead. A Set rather than "the last one":
   * a visitor who corrects a typo and then changes back would otherwise be
   * counted twice for the same address.
   */
  const reportedLeadsRef = useRef<Set<string>>(new Set())

  const durationLabel = campaign.durationDays
    ? t('promoCampaign.durationDays', { count: campaign.durationDays })
    : t('promoCampaign.durationMonths', { count: campaign.durationMonths })

  const title = campaign.metadata.publicTitle
    ? campaign.metadata.publicTitle
    : t('promoCampaign.defaultTitle', { plan: campaign.planName, duration: durationLabel })
  const subtitle = campaign.metadata.publicSubtitle ?? t('promoCampaign.defaultSubtitle')
  const badge = campaign.metadata.badgeText ?? t('promoCampaign.defaultBadge')
  const percent = claimedPercent(campaign.remaining, campaign.maxClaims)

  // The claimant's address is passed in rather than read from state: the
  // one-click path sets it in the same tick it starts the claim, so a closure
  // over `email` would still hold the previous value.
  //
  // `accountWasCreated` comes from the verify response when the API supplied
  // one; `viaOtp` is only the fallback, and it is a coarse one — a guest going
  // through OTP may well have had an account already, and telling them we just
  // made one would be untrue.
  const runClaim = useCallback(
    async (claimantEmail: string, viaOtp: boolean, accountWasCreated?: boolean) => {
      const createdAccount = accountWasCreated ?? viaOtp
      // The COPY has to decide something, so it falls back. The metric must
      // not: a guess reported as `account_created` is indistinguishable from a
      // fact in the funnel it feeds, so an unknown answer is simply absent.
      // Signed in already IS known — that account cannot have been just made.
      const knownAccountCreated = viaOtp ? accountWasCreated : false
      setPhase({ kind: 'claiming' })
      try {
        const result = await claimFn(campaign.slug)
        setPhase({
          kind: 'claimed',
          until: result.subscription.complimentaryEndsAt ?? result.subscription.currentPeriodEnd,
          email: claimantEmail,
          accountWasCreated: createdAccount,
        })
        trackEvent(ANALYTICS_EVENTS.campaignClaimed, {
          slug: campaign.slug,
          surface,
          path: viaOtp ? 'otp' : 'signed_in',
          ...(knownAccountCreated === undefined ? {} : { account_created: knownAccountCreated }),
        })
        setToastOpen(true)
        onClaimed?.()
      } catch (error) {
        reportHandledException(error, {
          source: 'features/promotion-campaign/campaign-banner.tsx',
        })
        const code = error instanceof CampaignApiError ? error.code : 'unknown'
        setPhase(
          code === EMAIL_VERIFICATION_REQUIRED_ERROR
            ? { kind: 'verifyEmail', claimantEmail }
            : { kind: 'failed', code }
        )
        // Without this a campaign that has filled up and one that is broken for
        // everybody look identical: both simply stop producing claims.
        trackEvent(ANALYTICS_EVENTS.campaignClaimFailed, {
          slug: campaign.slug,
          surface,
          reason: code,
        })
      }
    },
    [campaign.slug, claimFn, onClaimed, surface]
  )

  const submitEmail = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const trimmed = email.trim().toLowerCase()
      if (!EMAIL_PATTERN.test(trimmed)) {
        setFieldError(t('promoCampaign.errEmailFormat'))
        return
      }
      setFieldError(null)
      setEmail(trimmed)
      setPhase({ kind: 'verifying' })
      // Once per ADDRESS, not once per submit. The dialog can be dismissed and
      // the typed address is deliberately kept, so re-submitting is one
      // visitor trying again — and this maps to the standard Pixel `Lead`,
      // where a retry counted twice inflates cost-per-lead in exactly the
      // direction that makes a flow people struggle with look successful.
      if (!reportedLeadsRef.current.has(trimmed)) {
        reportedLeadsRef.current.add(trimmed)
        trackEvent(ANALYTICS_EVENTS.campaignEmailSubmitted, { slug: campaign.slug, surface })
      }
      authModal.open({
        prefillEmail: trimmed,
        // Stay on the page: the section itself swaps to the claimed state, and
        // navigating away would hide the result the visitor came for.
        redirectTo: null,
        notice: t('promoCampaign.authNotice'),
        campaign: {
          planName: campaign.planName,
          durationLabel,
          email: trimmed,
        },
        onAuthenticated: (outcome) => {
          void runClaim(trimmed, true, outcome.accountWasCreated)
        },
      })
    },
    [authModal, campaign.planName, campaign.slug, durationLabel, email, runClaim, surface, t]
  )

  const claimDirectly = useCallback(() => {
    // The account is already known server-side; the address is only used by the
    // "we created your account" copy, which does not apply to someone signed in.
    const claimantEmail = ''
    setEmail(claimantEmail)
    // Signed in already: the account definitely predates this claim, so the
    // copy must not tell them it was just created.
    void runClaim(claimantEmail, false)
  }, [runClaim, viewer])

  const openWaitlist = useCallback(() => {
    trackEvent(ANALYTICS_EVENTS.campaignWaitlistClick, { slug: campaign.slug, surface })
    authModal.open({ redirectTo: null, notice: t('promoCampaign.waitlistNotice') })
  }, [authModal, campaign.slug, surface, t])

  // The modal can be dismissed at any step; returning to idle keeps the typed
  // address so the visitor can send again without retyping it.
  const modalIsOpen = authModal.isOpen
  const previousModalOpen = useRef(modalIsOpen)
  if (previousModalOpen.current && !modalIsOpen && phase.kind === 'verifying') {
    setPhase({ kind: 'idle' })
  }
  previousModalOpen.current = modalIsOpen

  const dataState = useMemo(() => {
    if (phase.kind === 'claimed') return 'claimed'
    if (phase.kind === 'failed') return 'failed'
    if (phase.kind === 'verifying' || phase.kind === 'claiming') return 'sending'
    // The returning-visitor panels outrank the campaign's own state: someone who
    // cannot claim should be told what they already have, not that the wave is
    // full. `unknown`/`guest` fall through to the campaign state, so an
    // unresolved viewer never blanks the panel.
    if (viewer.kind === 'owned') return 'owned'
    if (viewer.kind === 'expired') return 'expired'
    if (viewer.kind === 'subscribed') return 'subscribed'
    return campaign.state === 'open' ? 'live' : 'full'
  }, [campaign.state, phase, viewer])

  // Read at fire time rather than captured, so the observer is armed once by
  // the node attaching instead of being torn down and rebuilt by every
  // unrelated state change.
  const dataStateRef = useRef(dataState)
  dataStateRef.current = dataState
  const viewedRef = useRef(false)

  /**
   * Arms the impression event when the section actually mounts.
   *
   * A callback ref rather than an effect: this panel is not rendered at all
   * until the viewer lookup resolves, and the two renders on either side of
   * that can produce an identical `dataState` — an effect keyed on state would
   * simply never re-run and the impression would be lost. The node attaching is
   * the event we care about, and that is exactly what a callback ref reports.
   */
  const observeSection = useCallback(
    (node: HTMLElement | null) => {
      if (!node || viewedRef.current) return
      if (typeof IntersectionObserver === 'undefined') {
        // No observer (old browser): count the render as the impression rather
        // than losing the funnel's denominator for those visitors entirely.
        viewedRef.current = true
        trackEventWhenReady(ANALYTICS_EVENTS.campaignViewed, {
          slug: campaign.slug,
          surface,
          state: dataStateRef.current,
        })
        return
      }
      const observer = new IntersectionObserver(
        (entries) => {
          // Re-checked inside the callback, not only before arming: an observer
          // can deliver a batch spanning the moment it was disconnected.
          if (viewedRef.current) return
          if (!entries.some((entry) => entry.isIntersecting)) return
          viewedRef.current = true
          observer.disconnect()
          // Deferred rather than fired: the panel can be on screen before the
          // `afterInteractive` GA/Pixel snippets run, and this event is the
          // denominator the whole campaign funnel is measured against.
          trackEventWhenReady(ANALYTICS_EVENTS.campaignViewed, {
            slug: campaign.slug,
            surface,
            state: dataStateRef.current,
          })
        },
        // Half the panel: a strip of it clipping into view at the bottom of the
        // window is not somebody looking at the offer.
        { threshold: 0.5 }
      )
      observer.observe(node)
      return () => observer.disconnect()
    },
    [campaign.slug, surface]
  )

  const isReturning =
    viewer.kind === 'owned' || viewer.kind === 'expired' || viewer.kind === 'subscribed'
  const showClaimForm =
    campaign.state === 'open' &&
    phase.kind !== 'claimed' &&
    phase.kind !== 'failed' &&
    phase.kind !== 'verifyEmail' &&
    !isReturning
  // The quota/deadline/meter row is for people who can still claim; for a
  // returning visitor the whole panel is about their own plan instead. It stays
  // visible in `claimed` — the design keeps the wave's numbers on screen at the
  // moment someone joins it.
  const showCampaignFacts = !isReturning

  // Fail CLOSED while the viewer is unresolved. On a surface that hides itself
  // from paying subscribers, rendering during the lookup (or after it failed)
  // would flash a claim entry at exactly the people it is meant to spare — and
  // the lookup degrades to `guest` on any error, so "not yet known" and "is a
  // guest" would otherwise be indistinguishable.
  if (hideForPaidSubscribers) {
    if (viewer.kind === 'unknown') return null
    if (viewer.kind === 'subscribed' && viewer.subscription.kind === 'paid') return null
  }

  return (
    <section
      ref={observeSection}
      className={className ? `campaign ${className}` : 'campaign'}
      id={sectionId}
      data-campaign
      data-state={dataState}
      data-slug={campaign.slug}
      data-remaining={campaign.remaining ?? undefined}
      data-quota={campaign.maxClaims ?? undefined}
      data-deadline={campaign.redeemEndsAt}
    >
      <div className="camp-wash" aria-hidden="true" />
      <div className="camp-scrim" aria-hidden="true" />
      <div className="camp-container">
        <div className="camp-grid">
          <div className="camp-copy">
            <span className="camp-badge">{badge}</span>
            <h2 className="camp-h">{title}</h2>
            <p className="camp-lead">{subtitle}</p>
          </div>

          <div className="camp-act">
            {showCampaignFacts ? (
              <div className="camp-facts">
                {campaign.remaining !== null && campaign.maxClaims !== null ? (
                  <div className="camp-fact">
                    <span className="k">{t('promoCampaign.remainingLabel')}</span>
                    <span className="v">
                      <span data-camp="remaining">{campaign.remaining}</span>{' '}
                      <small>
                        / <span data-camp="quota">{campaign.maxClaims}</span>
                      </small>
                    </span>
                  </div>
                ) : null}
                <div className="camp-fact">
                  <span className="k">{t('promoCampaign.deadlineLabel')}</span>
                  <span className="v">
                    {t('promoCampaign.deadlineValue', {
                      date: formatCampaignDate(campaign.redeemEndsAt),
                    })}
                  </span>
                </div>
              </div>
            ) : null}

            {showCampaignFacts && percent !== null ? (
              <div
                className="camp-meter"
                role="img"
                aria-label={t('promoCampaign.meterAria', {
                  claimed: (campaign.maxClaims ?? 0) - (campaign.remaining ?? 0),
                  quota: campaign.maxClaims ?? 0,
                })}
              >
                <i data-camp="meter" style={{ width: `${percent}%` }} />
              </div>
            ) : null}

            {showClaimForm ? (
              <div className="camp-form-wrap">
                <p className="camp-label">{t('promoCampaign.formLabel')}</p>
                {viewer.kind === 'eligible' ? (
                  <Button
                    className="camp-cta"
                    disabled={phase.kind === 'claiming'}
                    onClick={claimDirectly}
                  >
                    <span className="camp-spin" aria-hidden="true" />
                    {phase.kind === 'claiming'
                      ? t('promoCampaign.ctaClaiming')
                      : t('promoCampaign.ctaSignedIn')}
                  </Button>
                ) : (
                  <form className="camp-form" noValidate onSubmit={submitEmail}>
                    <Field
                      label={t('promoCampaign.emailLabel')}
                      htmlFor="campaign-email"
                      error={fieldError}
                    >
                      <Input
                        id="campaign-email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder={t('promoCampaign.emailPlaceholder')}
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value)
                          // Clearing on the first keystroke matches the design:
                          // the message describes what to fix, not a verdict to
                          // keep staring at.
                          if (fieldError) setFieldError(null)
                        }}
                      />
                    </Field>
                    <Button type="submit" className="camp-cta" disabled={phase.kind !== 'idle'}>
                      <span className="camp-spin" aria-hidden="true" />
                      {phase.kind === 'idle'
                        ? t('promoCampaign.cta', {
                            plan: campaign.planName,
                            duration: durationLabel,
                          })
                        : t('promoCampaign.ctaSending')}
                    </Button>
                  </form>
                )}
                <p className="camp-fine">
                  <Trans i18nKey="promoCampaign.fine" ns="web" components={{ b: <b /> }} />
                </p>
              </div>
            ) : null}

            {phase.kind === 'claimed' ? (
              <div className="camp-claimed">
                <div className="cc-mark" aria-hidden="true">
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <p className="cc-head" tabIndex={-1} data-camp="claimed-head">
                  {t('promoCampaign.claimedHead', {
                    plan: campaign.planName,
                    date: formatCampaignDate(phase.until),
                  })}
                </p>
                <p className="cc-sub">
                  {phase.accountWasCreated ? (
                    <Trans
                      i18nKey="promoCampaign.claimedSubNewAccount"
                      ns="web"
                      values={{ email: phase.email }}
                      components={{ b: <b /> }}
                    />
                  ) : (
                    <Trans
                      i18nKey="promoCampaign.claimedSubExistingAccount"
                      ns="web"
                      components={{ b: <b /> }}
                    />
                  )}
                </p>
                <div className="cc-next">
                  <ButtonLink as={Link} variant="secondary" href="/dictation#download">
                    {t('promoCampaign.claimedDownload')}
                  </ButtonLink>
                  <ButtonLink as={Link} variant="secondary" href={APP_ROUTES.transcriptions}>
                    {t('promoCampaign.claimedUpload')}
                  </ButtonLink>
                </div>
              </div>
            ) : null}

            {viewer.kind === 'owned' && phase.kind === 'idle' ? (
              <div className="camp-owned">
                <div className="cc-mark" aria-hidden="true">
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <p className="cc-head" tabIndex={-1}>
                  {t('promoCampaign.ownedHead')}
                </p>
                <dl className="cm-rows">
                  <div>
                    <dt>{t('promoCampaign.ownedPlanLabel')}</dt>
                    <dd>{t('promoCampaign.giftPlanName', { plan: viewer.planName })}</dd>
                  </div>
                  <div>
                    <dt>{t('promoCampaign.ownedUntilLabel')}</dt>
                    <dd>{formatCampaignDate(viewer.accessEndsAt)}</dd>
                  </div>
                  {/* The number a returning visitor actually came for — a date
                      alone makes them do the subtraction. */}
                  <div className="cm-left">
                    <dt>{t('promoCampaign.ownedLeftLabel')}</dt>
                    <dd>
                      {t('promoCampaign.ownedLeftDays', {
                        count: daysUntil(viewer.accessEndsAt) ?? 0,
                      })}
                    </dd>
                  </div>
                </dl>
                <p className="cc-sub">
                  <Trans i18nKey="promoCampaign.ownedNote" ns="web" components={{ b: <b /> }} />
                </p>
                <div className="cc-next">
                  <ButtonLink as={Link} variant="secondary" href={APP_ROUTES.transcriptions}>
                    {t('promoCampaign.ownedOpenApp')}
                  </ButtonLink>
                  <ButtonLink as={Link} variant="secondary" href={APP_ROUTES.account}>
                    {t('promoCampaign.viewMyPlan')}
                  </ButtonLink>
                </div>
              </div>
            ) : null}

            {viewer.kind === 'expired' && phase.kind === 'idle' ? (
              /* No tick: they hold nothing now, so this cannot carry the same
                 weight as「還剩 47 天」. It is the most natural upgrade moment,
                 so the single action points at the plans. */
              <div className="camp-expired">
                <p className="cc-head" tabIndex={-1}>
                  {t('promoCampaign.ownedHead')}
                </p>
                <dl className="cm-rows">
                  <div>
                    <dt>{t('promoCampaign.expiredPlanLabel')}</dt>
                    <dd>{t('promoCampaign.giftPlanName', { plan: viewer.planName })}</dd>
                  </div>
                  {viewer.accessEndsAt ? (
                    <div>
                      <dt>{t('promoCampaign.ownedUntilLabel')}</dt>
                      <dd>{formatCampaignDate(viewer.accessEndsAt)}</dd>
                    </div>
                  ) : null}
                  <div className="cm-off">
                    <dt>{t('promoCampaign.expiredStatusLabel')}</dt>
                    <dd>{t('promoCampaign.expiredStatusValue')}</dd>
                  </div>
                </dl>
                <p className="cc-sub">{t('promoCampaign.expiredNote')}</p>
                <div className="cc-next">
                  <ButtonLink as={Link} className="camp-cta" href="/pricing">
                    {t('promoCampaign.viewPlans')}
                  </ButtonLink>
                </div>
              </div>
            ) : null}

            {viewer.kind === 'subscribed' && phase.kind === 'idle' ? (
              /* Also no tick — this is not a success, it explains why the offer
                 cannot apply and shows what they already have. Neither the
                 heading nor the body says「付費訂閱」or「扣款日」: both are
                 false for a gift from another campaign. */
              <div className="camp-subscribed">
                {/* The two variants are blocked for DIFFERENT reasons, so the
                    heading and the explanation are different sentences — not
                    one shared line with a swapped noun. */}
                <p className="cc-head" tabIndex={-1}>
                  {viewer.subscription.kind === 'gift'
                    ? t('promoCampaign.subscribedGiftHead')
                    : t('promoCampaign.subscribedPaidHead')}
                </p>
                <p className="cc-sub">
                  <Trans
                    i18nKey={
                      viewer.subscription.kind === 'gift'
                        ? 'promoCampaign.subscribedGiftNote'
                        : 'promoCampaign.subscribedPaidNote'
                    }
                    ns="web"
                    components={{ b: <b /> }}
                  />
                </p>
                <dl className="cm-rows">
                  <div>
                    <dt>{t('promoCampaign.subscribedPlanLabel')}</dt>
                    <dd>
                      {viewer.subscription.kind === 'gift'
                        ? t('promoCampaign.giftPlanName', {
                            plan: viewer.subscription.planName,
                          })
                        : t('promoCampaign.paidPlanName', {
                            plan: viewer.subscription.planName,
                            cycle: t(
                              viewer.subscription.cycle === 'yearly'
                                ? 'promoCampaign.cycleYearly'
                                : 'promoCampaign.cycleMonthly'
                            ),
                          })}
                    </dd>
                  </div>
                  {viewer.subscription.date ? (
                    <div>
                      {/* Telling someone who pays nothing about a "next charge"
                          would make them think money is about to leave. */}
                      <dt>
                        {viewer.subscription.dateKind === 'charge'
                          ? t('promoCampaign.nextChargeLabel')
                          : t('promoCampaign.ownedUntilLabel')}
                      </dt>
                      <dd>{formatCampaignDate(viewer.subscription.date)}</dd>
                    </div>
                  ) : null}
                  {/* Gift only: what they actually came to find out is when they
                      are free to join the next campaign. A paid plan has no such
                      countdown — it simply renews. */}
                  {viewer.subscription.kind === 'gift' && viewer.subscription.date ? (
                    <div className="cm-left">
                      <dt>{t('promoCampaign.ownedLeftLabel')}</dt>
                      <dd>
                        {t('promoCampaign.ownedLeftDays', {
                          count: daysUntil(viewer.subscription.date) ?? 0,
                        })}
                      </dd>
                    </div>
                  ) : null}
                </dl>
                <div className="cc-next">
                  <ButtonLink as={Link} variant="secondary" href={APP_ROUTES.account}>
                    {t('promoCampaign.viewMyPlan')}
                  </ButtonLink>
                </div>
              </div>
            ) : null}

            {phase.kind === 'failed' ? (
              <CampaignFailure code={phase.code} campaign={campaign} onWaitlist={openWaitlist} />
            ) : null}

            {phase.kind === 'verifyEmail' ? (
              <div className="camp-form-wrap" data-testid="campaign-verify-email">
                <EmailVerificationStep
                  intro={t('promoCampaign.verifyDesc')}
                  submitLabel={t('promoCampaign.verifyAndClaim')}
                  onVerified={() => runClaim(phase.claimantEmail, false)}
                  onCancel={() =>
                    setPhase({ kind: 'failed', code: EMAIL_VERIFICATION_REQUIRED_ERROR })
                  }
                  testIdPrefix="campaign-verify"
                  requestFn={requestVerificationFn}
                  verifyFn={verifyFn}
                />
              </div>
            ) : null}

            {phase.kind !== 'claimed' &&
            phase.kind !== 'failed' &&
            campaign.state !== 'open' &&
            !isReturning ? (
              <div className="camp-full">
                <p className="cf-line">{t('promoCampaign.fullLine')}</p>
                {campaign.nextWaveOpensAt ? (
                  <p className="camp-next-wave">
                    {t('promoCampaign.nextWave', {
                      date: formatCampaignDate(campaign.nextWaveOpensAt),
                      count: daysUntil(campaign.nextWaveOpensAt) ?? 0,
                    })}
                  </p>
                ) : null}
                <p>
                  {campaign.maxClaims !== null
                    ? t('promoCampaign.fullBody', { quota: campaign.maxClaims })
                    : t('promoCampaign.fullBodyUncapped')}
                </p>
                <Button variant="secondary" onClick={openWaitlist}>
                  {t('promoCampaign.fullCta')}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* The dialog already showed a tick; this confirms the swap that happened
          behind it, where the visitor was not looking. */}
      <CampaignToast
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        message={
          phase.kind === 'claimed'
            ? t('promoCampaign.toast', {
                plan: campaign.planName,
                date: formatCampaignDate(phase.until),
              })
            : ''
        }
      />
    </section>
  )
}

/**
 * OTP succeeded but the claim did not. Deliberately NO tick and no activation
 * animation — the visitor must not be told they got something they did not.
 */
function CampaignFailure({
  code,
  campaign,
  onWaitlist,
}: {
  code: CampaignApiError['code']
  campaign: PublicCampaign
  /** Shared with the full-campaign panel so both waitlist CTAs report alike. */
  onWaitlist: () => void
}) {
  const { t } = useTranslation('web')

  const copy = {
    promotion_claim_limit_reached: {
      line: t('promoCampaign.failedCapLine'),
      body: t('promoCampaign.failedCapBody'),
      action: 'register' as const,
    },
    promotion_already_claimed: {
      line: t('promoCampaign.failedAlreadyLine'),
      body: t('promoCampaign.failedAlreadyBody'),
      action: 'app' as const,
    },
    live_subscription_exists: {
      line: t('promoCampaign.failedSubscribedLine'),
      body: t('promoCampaign.failedSubscribedBody'),
      action: 'app' as const,
    },
    promotion_unavailable: {
      line: t('promoCampaign.failedUnavailableLine'),
      body: t('promoCampaign.failedUnavailableBody'),
      action: 'app' as const,
    },
    account_deactivated: {
      line: t('promoCampaign.failedDeactivatedLine'),
      body: t('promoCampaign.failedDeactivatedBody'),
      action: 'none' as const,
    },
    // The invite link in that email proves the inbox, which is exactly what
    // this account is missing — so the email is the way through, not a retry.
    [EMAIL_VERIFICATION_REQUIRED_ERROR]: {
      line: t('promoCampaign.failedVerifyEmailLine'),
      body: t('promoCampaign.failedVerifyEmailBody'),
      action: 'none' as const,
    },
    rate_limited: {
      line: t('promoCampaign.failedRateLimitedLine'),
      body: t('promoCampaign.failedRateLimitedBody'),
      action: 'none' as const,
    },
    unknown: {
      line: t('promoCampaign.failedGenericLine'),
      body: t('promoCampaign.failedGenericBody'),
      action: 'none' as const,
    },
  }[code]

  return (
    <div className="camp-failed" data-camp="failed" data-reason={code}>
      <p className="cf-line" role="alert">
        {copy.line}
      </p>
      <p>{copy.body}</p>
      {copy.action === 'app' ? (
        <ButtonLink as={Link} variant="secondary" href={APP_ROUTES.transcriptions}>
          {t('promoCampaign.failedGoToApp')}
        </ButtonLink>
      ) : null}
      {copy.action === 'register' ? (
        <Button variant="secondary" onClick={onWaitlist}>
          {t('promoCampaign.fullCta')}
        </Button>
      ) : null}
      {campaign.nextWaveOpensAt && code === 'promotion_claim_limit_reached' ? (
        <p className="camp-next-wave">
          {t('promoCampaign.nextWave', {
            date: formatCampaignDate(campaign.nextWaveOpensAt),
            count: daysUntil(campaign.nextWaveOpensAt) ?? 0,
          })}
        </p>
      ) : null}
    </div>
  )
}
